import amplitude from 'amplitude-js';

import { appEventTypes } from 'events/app';
import { MESSAGE_CREATE_UPDATE_SENDING } from 'events/messages';
import { uiEventTypes } from 'events/ui';
import { userEventTypes } from 'events/user';
import { selfServiceEventTypes } from 'events/selfService';
import { Actions } from 'enums/gladlyapi';

// Amplitude Event Types
const LOG_EVENT = 'logEvent';
const IDENTIFY = 'identify';

/*
 * Map action types to analytics event names
 *
 * Choose event names carefully as they cannot be changed once used in production.
 *
 * Each event map item should return the following object:
 * [ { eventType: ..., eventPayload: ... }]
 */
const generateActionEventMap = () => ({
  [appEventTypes.AUTHENTICATION_SUCCESS]: (getState, { payload: { gladlyCustomerId } }) => {
    if (gladlyCustomerId) {
      let identity = new amplitude.Identify().set('customerId', gladlyCustomerId);
      return [
        {
          eventType: IDENTIFY,
          eventPayload: [identity],
        },
      ];
    }
  },

  [appEventTypes.APP_INITIALIZED]: getState => {
    let config = getState().configuration.config;
    let bucketId = getState().configuration.abTestingBucket;

    let identity = new amplitude.Identify()
      .set('appId', config.appId)
      .set('orgId', config.orgId)
      .set('bucketId', bucketId);
    return [
      {
        eventType: IDENTIFY,
        eventPayload: [identity],
      },
    ];
  },
  [selfServiceEventTypes.SS_UPDATE_SELECTED_ANSWER_SUCCESS]: (getState, { payload: { answer } }) => {
    if (answer) {
      return [
        {
          eventType: LOG_EVENT,
          eventPayload: ['Search Result Selected', answer.id],
        },
      ];
    }
  },
  [selfServiceEventTypes.SS_UPDATE_SEARCH_RESULTS_SUCCESS]: (getState, { payload: { searchTerm, results } }) => {
    if (searchTerm) {
      let resultIds = [];
      for (let i = 0; i < results.length; i++) {
        resultIds.push(results[i].id);
      }

      return [
        {
          eventType: LOG_EVENT,
          eventPayload: ['Searched', { searchTerm, resultIds }],
        },
      ];
    }
  },
  [Actions.GLADLY_API_SHOW]: (getState, { payload: { isVisible } }) => {
    return [
      {
        eventType: LOG_EVENT,
        eventPayload: ['API show', { isVisible }],
      },
    ];
  },
  [uiEventTypes.WIDGET_UNMINIMIZE]: getState => {
    const user = getState().user;

    if (!user || !user.id) {
      // A user has an id once a user goes through
      // onboarding or send their first message
      return [
        {
          eventType: LOG_EVENT,
          eventPayload: ['Widget Opened'],
        },
      ];
    }
  },
  [userEventTypes.USER_CREATE]: (getState, { payload: { hasOnboarded } }) => {
    // This comes from `createOnboardingUser` in `createUser.js#30`
    // The value will be set to 'true' if the user just submitted onboarding
    if (hasOnboarded) {
      return [
        {
          eventType: LOG_EVENT,
          eventPayload: ['Onboarding Form Submitted'],
        },
      ];
    }
  },
  [MESSAGE_CREATE_UPDATE_SENDING]: (getState, action) => {
    const config = getState().configuration.config;
    let isCustomerAuthenticated;
    if (config.authenticatedTagName) {
      isCustomerAuthenticated = checkIfCustomerAuthenticated(config.authenticatedTagName);
    }

    return [
      {
        eventType: LOG_EVENT,
        eventPayload: [
          'Message Sent',
          {
            isCustomerAuthenticated,
            messageId: action.payload.id,
          },
        ],
      },
    ];
  },
});

export default context => {
  amplitude.getInstance().init(context.config.amplitudeApiKey);

  const actionEventMapper = generateActionEventMap();

  return store => next => action => handleAction(store.getState.bind(store), next, action, actionEventMapper);
};

function handleAction(getState, next, action, actionEventMapper) {
  if (actionEventMapper[action.type]) {
    const analytics = actionEventMapper[action.type](getState, action);
    if (analytics) {
      analytics.forEach(a => handleAnalytic(a));
    }
  }

  return next(action);
}

function handleAnalytic(analytic) {
  const type = analytic.eventType;

  // In case the eventType was not specified or set to `null`, ignore this individual spec.
  if (type && type.length) {
    const fields = analytic.eventPayload || {};

    if (fields instanceof Error) return console.warn(fields); // eslint-disable-line no-console

    emit(type, fields);
  }
}

function emit(type, fields) {
  const client = amplitude.getInstance();
  if (client && typeof client[type] === 'function') {
    client[type](...fields);
  }
}
/*
  checkIfCustomerAuthenticated is to track if a user
  is authenticated on our customer's website. It
  it to give Gladly an estimation of how often a user
  is authenticated on our customer's website when chatting
  in.
*/
function checkIfCustomerAuthenticated(tagName) {
  const elements = document.getElementsByTagName(tagName);
  if (elements.length) {
    return 'authenticated';
  }
  return 'not_authenticated';
}
